/**
 * Static Zürich postal-code → approximate coordinates + Quartier label.
 * Local-by-design: resolves the family profile's PLZ to weather coordinates
 * without a geocoding dependency. City-wide coordinate spread is negligible
 * for a daily forecast. Extend region by region as Albis grows.
 */

const ZURICH_CENTER = { latitude: 47.3769, longitude: 8.5417 };

/** @type {Record<string, { latitude: number, longitude: number, quartier: string }>} */
const PLZ_MAP = {
  "8001": { latitude: 47.3717, longitude: 8.5423, quartier: "Altstadt" },
  "8002": { latitude: 47.3626, longitude: 8.5318, quartier: "Enge" },
  "8003": { latitude: 47.3686, longitude: 8.5142, quartier: "Wiedikon" },
  "8004": { latitude: 47.3776, longitude: 8.5256, quartier: "Aussersihl" },
  "8005": { latitude: 47.3895, longitude: 8.5189, quartier: "Industriequartier" },
  "8006": { latitude: 47.3869, longitude: 8.5436, quartier: "Unterstrass" },
  "8008": { latitude: 47.3548, longitude: 8.5556, quartier: "Seefeld" },
  "8032": { latitude: 47.364, longitude: 8.566, quartier: "Hottingen" },
  "8037": { latitude: 47.3955, longitude: 8.5283, quartier: "Wipkingen" },
  "8038": { latitude: 47.3433, longitude: 8.534, quartier: "Wollishofen" },
  "8044": { latitude: 47.3742, longitude: 8.5664, quartier: "Fluntern" },
  "8045": { latitude: 47.3622, longitude: 8.4986, quartier: "Friesenberg" },
  "8046": { latitude: 47.4209, longitude: 8.518, quartier: "Affoltern" },
  "8047": { latitude: 47.3737, longitude: 8.4869, quartier: "Albisrieden" },
  "8048": { latitude: 47.3893, longitude: 8.4836, quartier: "Altstetten" },
  "8049": { latitude: 47.4022, longitude: 8.4977, quartier: "Höngg" },
  "8050": { latitude: 47.4106, longitude: 8.546, quartier: "Oerlikon" },
  "8051": { latitude: 47.4055, longitude: 8.5722, quartier: "Schwamendingen" },
  "8052": { latitude: 47.4243, longitude: 8.5466, quartier: "Seebach" },
  "8053": { latitude: 47.3597, longitude: 8.5949, quartier: "Witikon" },
  "8055": { latitude: 47.3262, longitude: 8.5128, quartier: "Leimbach" },
  "8057": { latitude: 47.4002, longitude: 8.544, quartier: "Unterstrass" },
  "8064": { latitude: 47.403, longitude: 8.4719, quartier: "Altstetten West" },
};

/** Suggested chips for the capture flow (most common central Kreise). */
export const COMMON_ZURICH_PLZ = [
  "8004",
  "8003",
  "8005",
  "8006",
  "8008",
  "8037",
  "8050",
  "8055",
];

/** Normalize free text to a 4-digit Swiss PLZ, or null. */
export function normalizePostalCode(input) {
  const match = /(\d{4})/.exec(String(input || ""));
  return match ? match[1] : null;
}

/**
 * Resolve a PLZ to coordinates + Quartier. Known Zürich codes resolve exactly;
 * other 8xxx codes fall back to the city center (quartier null).
 * @param {string} input
 * @returns {{ postalCode: string, latitude: number, longitude: number, quartier: string|null } | null}
 */
export function resolvePostalCode(input) {
  const plz = normalizePostalCode(input);
  if (!plz) return null;
  const hit = PLZ_MAP[plz];
  if (hit) {
    return {
      postalCode: plz,
      latitude: hit.latitude,
      longitude: hit.longitude,
      quartier: hit.quartier,
    };
  }
  if (plz.startsWith("8")) {
    return {
      postalCode: plz,
      latitude: ZURICH_CENTER.latitude,
      longitude: ZURICH_CENTER.longitude,
      quartier: null,
    };
  }
  return null;
}
